'use client';

import { useEffect, useRef } from 'react';
import type { PortalUser } from '@/lib/user-portal/types';
import type { BadgeAwardEntry, LevelAwardEntry, PointsAwardEntry } from '@/lib/gamification/types';
import { useCelebration } from './celebration-provider';
import type { CelebrationEvent } from './celebration-events';

interface SnapshotShape {
  totalPoints: number;
  rank: number;
  badgeCount: number;
  riskLevel: PortalUser['riskLevel'];
  timesCompromised: number;
}

interface DetectorInput {
  user: PortalUser | null;
  pointsAwards?: PointsAwardEntry[];
  badgeAwards?: BadgeAwardEntry[];
  levelAwards?: LevelAwardEntry[];
}

export function useCelebrationDetector({ user, pointsAwards = [], badgeAwards = [], levelAwards = [] }: DetectorInput) {
  const { enqueue } = useCelebration();
  const fired = useRef(false);

  useEffect(() => {
    if (!user || fired.current) return;
    fired.current = true;

    const snapKey = `portal:celebrations:snapshot:${user.id}`;
    let prev: SnapshotShape | null = null;
    try {
      const raw = localStorage.getItem(snapKey);
      if (raw) prev = JSON.parse(raw);
    } catch {}

    const next: SnapshotShape = {
      totalPoints: user.totalPoints,
      rank: user.rank,
      badgeCount: user.badgeCount,
      riskLevel: user.riskLevel,
      timesCompromised: user.timesCompromised,
    };

    const events: CelebrationEvent[] = [];

    if (prev) {
      if (next.rank < prev.rank) {
        events.push({
          id: `rank-up-${user.id}-${prev.rank}-${next.rank}`,
          kind: 'rank_climbed',
          rank: next.rank,
          delta: prev.rank - next.rank,
        });
      } else if (next.rank - prev.rank >= 2) {
        events.push({
          id: `rank-down-${user.id}-${prev.rank}-${next.rank}`,
          kind: 'rank_dropped',
          rank: next.rank,
          delta: next.rank - prev.rank,
        });
      }

      if (next.timesCompromised > prev.timesCompromised) {
        events.push({
          id: `compromised-${user.id}-${next.timesCompromised}`,
          kind: 'phishing_compromised',
          campaignName: 'Recent simulation',
        });
      }

      const riskOrder = { Low: 0, Medium: 1, High: 2 } as const;
      if (riskOrder[next.riskLevel] > riskOrder[prev.riskLevel]) {
        events.push({
          id: `risk-${user.id}-${prev.riskLevel}-${next.riskLevel}`,
          kind: 'risk_level_increased',
          level: next.riskLevel,
        });
      }
    }

    const userAwards = pointsAwards.filter((p) => p.employeeEmail === user.email);
    userAwards.slice(0, 3).forEach((p) => {
      if (p.points < 25) return;
      events.push({
        id: `points-${p.id}`,
        kind: 'points_awarded',
        points: p.points,
        activity: p.activity,
        totalPoints: user.totalPoints,
      });
    });

    badgeAwards
      .filter((b) => b.employeeEmail === user.email)
      .slice(0, 3)
      .forEach((b) => {
        events.push({
          id: `badge-${b.id}`,
          kind: 'badge_earned',
          badgeName: b.badgeName,
          badgeIcon: b.badgeIcon,
        });
      });

    levelAwards
      .filter((l) => l.employeeEmail === user.email)
      .slice(0, 1)
      .forEach((l) => {
        events.push({
          id: `level-${l.id}`,
          kind: 'level_up',
          levelName: l.levelName,
          levelIcon: l.levelIcon,
          pointsRequired: l.pointsRequired,
        });
      });

    if (events.length) enqueue(events);

    try {
      localStorage.setItem(snapKey, JSON.stringify(next));
    } catch {}
  }, [user, pointsAwards, badgeAwards, levelAwards, enqueue]);
}
